import { useState } from "react";
import { Link } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";
import { Send, Mail, Instagram, MapPin, Clock } from "lucide-react";
import { useT } from "@/context/I18nContext";

const input = "w-full px-4 py-3 rounded-lg border border-[color:var(--brunchi-stone-2)] bg-white text-sm focus:outline-none focus:ring-2 focus:ring-[color:var(--brunchi-green)]";

export default function Contact() {
  const { t } = useT();
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/contact", form);
      setSent(true);
      toast.success(t("contact.sent"));
    } catch (err) {
      toast.error(apiError(err, t("contact.failed")));
    } finally { setBusy(false); }
  };

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12 lg:py-16" data-testid="contact-page">
      <div className="grid lg:grid-cols-5 gap-12">
        <div className="lg:col-span-2">
          <div className="text-xs uppercase tracking-[0.25em] text-[color:var(--brunchi-green-hover)] mb-3">{t("contact.eyebrow")}</div>
          <h1 className="font-display text-4xl sm:text-5xl tracking-tight">{t("contact.title")}</h1>
          <p className="mt-4 text-[color:var(--brunchi-ink-soft)]">{t("contact.intro")}</p>
          <ul className="mt-8 space-y-4 text-sm">
            <li className="flex items-start gap-3" data-testid="contact-email">
              <Mail size={18} className="mt-0.5 text-[color:var(--brunchi-green-hover)]" />
              <a href="mailto:greg@brunchi.co" className="hover:underline">greg@brunchi.co</a>
            </li>
            <li className="flex items-start gap-3" data-testid="contact-instagram">
              <Instagram size={18} className="mt-0.5 text-[color:var(--brunchi-green-hover)]" />
              <a href="https://www.instagram.com/brunchicy" target="_blank" rel="noreferrer" className="hover:underline">@brunchicy</a>
            </li>
            <li className="flex items-start gap-3" data-testid="contact-location">
              <MapPin size={18} className="mt-0.5 text-[color:var(--brunchi-green-hover)]" />
              <span>{t("contact.location")}</span>
            </li>
            <li className="flex items-start gap-3" data-testid="contact-hours">
              <Clock size={18} className="mt-0.5 text-[color:var(--brunchi-green-hover)]" />
              <span>{t("contact.hours")}</span>
            </li>
          </ul>
          <p className="mt-8 text-sm text-[color:var(--brunchi-ink-soft)]">
            {t("contact.catering_hint")} <Link to="/corporate-catering" className="underline text-[color:var(--brunchi-green-hover)]" data-testid="contact-catering-link">{t("nav.catering")}</Link>
          </p>
        </div>

        <div className="lg:col-span-3 bg-white rounded-2xl border border-[color:var(--brunchi-stone)] p-6 sm:p-8">
          {sent ? (
            <div className="py-16 text-center" data-testid="contact-success">
              <div className="mx-auto w-14 h-14 rounded-full bg-[color:var(--brunchi-green-light)] flex items-center justify-center text-[color:var(--brunchi-green-hover)]"><Send size={22} /></div>
              <h2 className="mt-5 font-display text-2xl">{t("contact.thanks_title")}</h2>
              <p className="mt-2 text-[color:var(--brunchi-ink-soft)]">{t("contact.thanks_body")}</p>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-3" data-testid="contact-form">
              <div className="grid sm:grid-cols-2 gap-3">
                <input required placeholder={t("contact.f_name")} value={form.name} onChange={set("name")} className={input} data-testid="contact-name" />
                <input required type="email" placeholder={t("contact.f_email")} value={form.email} onChange={set("email")} className={input} data-testid="contact-email-input" />
                <input placeholder={t("contact.f_phone")} value={form.phone} onChange={set("phone")} className={`${input} sm:col-span-2`} data-testid="contact-phone" />
                <textarea required rows={6} minLength={5} placeholder={t("contact.f_message")} value={form.message} onChange={set("message")} className={`${input} sm:col-span-2`} data-testid="contact-message" />
              </div>
              <button disabled={busy} type="submit" data-testid="contact-submit-btn"
                className="w-full py-3.5 rounded-full pill-primary font-medium inline-flex items-center justify-center gap-2 disabled:opacity-40">
                <Send size={16} /> {busy ? t("contact.sending") : t("contact.send")}
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
